import React from 'react';
import Checkbox from '@sabre/spark-react-core/checkbox';
import Tooltip from '@sabre/spark-react-core/tooltip';
import {
   string,
   func,
   bool,
   number,
   object,
   array,
   arrayOf,
   shape,
} from 'prop-types';
import { OPERATOR_OPTIONS } from '../../../utils/constants.js';
import ComparisonInput from '../../comparison-input/comparison-input.jsx';
import MultiSelect from '../../multi-select/multi-select.jsx';
import RadioButtons from '../../radio-buttons/radio-buttons.jsx';
import { INPUT_TYPE } from './__helpers__/functions.js';

const Field = ({
   name,
   paddingClass,
   fullName,
   inputValue,
   inputType = INPUT_TYPE.TEXT,
   ...props
}) => {
   const widthClass =
      inputType === INPUT_TYPE.RADIO_BUTTON
         ? 'w-full h-full'
         : inputType === INPUT_TYPE.CHECK_BOX
         ? 'check-box-container'
         : 'w-half';

   return (
      <div
         key={name}
         className={`search-criteria-field-container ${paddingClass} ${widthClass}`}
      >
         <Tooltip
            aria-label={`Input field for ${name}`}
            toggleEl={
               <label className='input-label' htmlFor={name}>
                  {name}
               </label>
            }
         >
            {fullName}
         </Tooltip>

         <InputComponent
            inputType={inputType}
            value={inputValue}
            name={name}
            {...props}
         />
      </div>
   );
};

const InputComponent = ({
   inputType,
   name,
   value,
   field,
   onChange,
   ...props
}) => {
   const options = OPERATOR_OPTIONS;
   const defaultOperator = options[0]?.value;

   const handleChange = (value) => {
      onChange({
         value,
         field: field,
         operator: defaultOperator,
      });
   };

   switch (inputType) {
      case INPUT_TYPE.TEXT:
      case INPUT_TYPE.DATE_PICKER:
         return (
            <ComparisonInput
               id={name}
               className='input'
               aria-label={`Enter value for ${name}`}
               value={value}
               onChange={onChange}
               name={name}
               inputType={inputType}
               field={field}
               {...props}
            />
         );
      case INPUT_TYPE.MULTI_SELECT: {
         return (
            <div className='input'>
               <MultiSelect
                  placeHolder=''
                  className='multi-select-input'
                  showLabel={false}
                  enableSelectAll={false}
                  data-testid={`${name}-drop-down`}
                  name={`${name}-drop-down`}
                  selectedValues={value?.value || []}
                  multiSelectOnChangeHandler={(selectedValues) =>
                     handleChange(selectedValues)
                  }
                  {...props}
               />
            </div>
         );
      }

      case INPUT_TYPE.CHECK_BOX: {
         return (
            <Checkbox
               name={name}
               className={`${value === true ? 'spark-info' : ''}`}
               data-testid={`${name}-checkbox-id`}
               value={value}
               checked={value?.value}
               {...props}
               onChange={(event) => handleChange(event.target.checked)}
            />
         );
      }

      case INPUT_TYPE.RADIO_BUTTON: {
         const getRadioButtonData = (radioButtonOptions) => {
            return (
               radioButtonOptions?.map(({ key }) => ({
                  label: key,
                  value: key,
               })) || []
            );
         };

         return (
            <div className='radio-button-container'>
               <RadioButtons
                  radioButtonsConfig={{
                     radioButtonClassName:
                        'spark-pad-r-2 spark-mar-b-0 default-font-family',
                     radioButtonGroupClassName:
                        'search-criteria-justify-between',
                     radioButtonName: name,
                     radioButtons: getRadioButtonData(props.radioButtonOptions),
                     value: value?.key,
                     onChangeHandler: (_, value) => handleChange(value),
                  }}
               />
            </div>
         );
      }

      default:
         return null;
   }
};

Field.propTypes = {
   name: string.isRequired,
   paddingClass: string,
   fullName: string,
   onChange: func.isRequired,
   inputValue: object,
   isEnabled: bool,
   characterLimit: number,
   placeHolder: string,
   validation: string,
   dataType: string,
   inputType: string,
   sectionDetails: array,
};

InputComponent.propTypes = {
   inputType: string,
   name: string,
   value: object,
   field: string,
   onChange: func,
   radioButtonOptions: arrayOf(
      shape({
         key: string,
         value: string,
      })
   ),
};

export default Field;
